/* eslint-disable @typescript-eslint/no-explicit-any */
// src/pages/SecurityPage.tsx
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async"
import { useAuth } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import securityService, {
  type UserSecurityData,
  type ActiveSession,
  type TwoFactorSetup,
} from "../service/securityService";
import {
  Shield,
  Lock,
  Eye,
  EyeOff,
  Bell,
  Key,
  UserCheck,
  AlertTriangle,
  RefreshCw,
  LogOut,
  CheckCircle,
  XCircle,
  Loader2,
  Mail,
  Smartphone,
  Laptop,
  Tablet,
  QrCode,
  Copy,
  Download,
} from "lucide-react";

import Skeleton from "../components/Skeleton";
// import { SkeletonText } from '../components/Skeleton';

interface ChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const SecurityPage: React.FC = () => {
  const { isDark } = useDarkMode();
  const {
    user,
    isAuthenticated,
    changePassword,
    resendVerificationEmail,
    checkAccountStatus,
  } = useAuth();

  const [securityData, setSecurityData] = useState<UserSecurityData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [showSessions, setShowSessions] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Password change state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState<ChangePasswordForm>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{
    type: string;
    text: string;
  } | null>(null);

  // 2FA State
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState<TwoFactorSetup | null>(
    null,
  );
  const [verificationToken, setVerificationToken] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [step, setStep] = useState<"setup" | "verify" | "backup">("setup");

  // Disable 2FA State
  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
  const [disable2FACode, setDisable2FACode] = useState("");
  const [disable2FAPassword, setDisable2FAPassword] = useState("");
  const [showDisablePassword, setShowDisablePassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchSecurityData();
      fetchActiveSessions();
      fetchSecuritySummary();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (error || successMessage || passwordMessage) {
      const timer = setTimeout(clearMessages, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, successMessage, passwordMessage]);
  const fetchSecurityData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [securityData] = await Promise.all([
        securityService.getUserSecurityData(),
        checkAccountStatus(user?.email || ""),
      ]);

      const enhancedSecurityData = {
        ...securityData,
        emailVerified: user?.emailVerified || securityData.emailVerified,
        isLocked: !user?.active || securityData.isLocked,
        userRole: user?.role,
        accountCreated: user?.createdAt || securityData.accountCreated,
      };

      setSecurityData(enhancedSecurityData);
    } catch (err: any) {
      setError(
        err.message || "Failed to load security data. Please try again.",
      );
      console.error("Error fetching security data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveSessions = async () => {
    try {
      const sessions = await securityService.getActiveSessions();
      setActiveSessions(sessions);
    } catch (err: any) {
      console.error("Error fetching active sessions:", err);
      setError(err.message || "Failed to load active sessions");
    }
  };

  const fetchSecuritySummary = async () => {
    try {
      await securityService.getSecuritySummary();
    } catch (err: any) {
      console.error("Error fetching security summary:", err);
    }
  };

  // ========== RENDER SKELETON LOADERS ==========

  const renderSkeletonHeader = () => (
    <div className="text-center mb-8">
      <div className="flex justify-center items-center mb-4">
        <Skeleton variant="circle" width={48} height={48} />
      </div>
      <Skeleton variant="text" width="40%" className="h-8 mx-auto mb-2" />
      <Skeleton variant="text" width="60%" className="h-4 mx-auto" />
    </div>
  );

  const renderSkeletonSecurityStatus = () => (
    <div
      className={`rounded-lg shadow-lg p-6 mb-8 ${
        isDark
          ? "bg-gray-800 border border-gray-700"
          : "bg-white border border-blue-100"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Skeleton variant="circle" width={20} height={20} />
          <Skeleton variant="text" width={150} className="h-6" />
        </div>
        <Skeleton variant="text" width={120} height={24} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="space-y-2">
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
          </div>
        ))}
      </div>
    </div>
  );

  const renderSkeletonSecuritySettings = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className={`rounded-lg shadow-md p-6 ${
            isDark
              ? "bg-gray-800 border border-gray-700"
              : "bg-white border border-blue-50"
          }`}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Skeleton variant="circle" width={40} height={40} />
              <div className="space-y-2">
                <Skeleton variant="text" width={120} className="h-4" />
                <Skeleton variant="text" width={180} />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Skeleton variant="text" width={100} height={24} />
            <Skeleton variant="button" width={80} height={32} />
          </div>
        </div>
      ))}
    </div>
  );

  const renderSkeletonSessions = () => (
    <div
      className={`rounded-lg shadow-lg p-6 mb-6 ${
        isDark
          ? "bg-gray-800 border border-gray-700"
          : "bg-white border border-blue-100"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <Skeleton variant="text" width={200} className="h-6" />
        <Skeleton variant="button" width={180} height={32} />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className={`flex items-center justify-between p-3 rounded-lg ${
              isDark ? "bg-gray-700/50" : "bg-blue-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <Skeleton variant="circle" width={40} height={40} />
              <div className="space-y-2">
                <Skeleton variant="text" width={100} />
                <Skeleton variant="text" width={150} />
              </div>
            </div>
            <Skeleton variant="circle" width={24} height={24} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderSkeletonActivity = () => (
    <div
      className={`rounded-lg shadow-lg p-6 ${
        isDark
          ? "bg-gray-800 border border-gray-700"
          : "bg-white border border-blue-100"
      }`}
    >
      <Skeleton variant="text" width={200} className="h-6 mb-4" />
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className={`flex items-center justify-between p-3 rounded-lg ${
              isDark ? "bg-gray-700/50" : "bg-blue-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <Skeleton variant="circle" width={40} height={40} />
              <div className="space-y-2">
                <Skeleton variant="text" width={120} />
                <Skeleton variant="text" width={180} />
              </div>
            </div>
            <Skeleton variant="text" width={60} height={24} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderSkeletonTips = () => (
    <div
      className={`mt-8 rounded-lg p-6 ${
        isDark
          ? "bg-blue-900/20 border border-blue-800"
          : "bg-blue-100 border border-blue-200"
      }`}
    >
      <Skeleton variant="text" width={100} className="h-5 mb-3" />
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} variant="text" width="100%" />
        ))}
      </div>
    </div>
  );

  // ========== RENDER LOADING STATE ==========

  if (loading) {
    return (
      <div
        className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
          isDark
            ? "bg-gray-900 text-white"
            : "bg-gradient-to-br from-sky-400 to-blue-600 text-gray-800"
        }`}
      >
        <div className="max-w-6xl mx-auto">
          {/* Header Skeleton */}
          {renderSkeletonHeader()}

          {/* Security Status Skeleton */}
          {renderSkeletonSecurityStatus()}

          {/* Security Settings Skeleton */}
          {renderSkeletonSecuritySettings()}

          {/* Sessions Skeleton (always show in loading state) */}
          {renderSkeletonSessions()}

          {/* Recent Activity Skeleton */}
          {renderSkeletonActivity()}

          {/* Security Tips Skeleton */}
          {renderSkeletonTips()}
        </div>
      </div>
    );
  }

  // ========== HANDLER FUNCTIONS ==========
  const handleEnableTwoFactor = async () => {
    try {
      setActionLoading("2fa");
      setError(null);
      setSuccessMessage(null);

      console.log("Starting 2FA enable process...");

      const data = await securityService.enableTwoFactor();
      console.log("2FA API Response:", data);

      if (!data) {
        throw new Error("No data received from server");
      }

      const normalizedData = {
        ...data,
        secret: data.secret || data.manualEntryCode || "",
        manualEntryCode: data.manualEntryCode || data.secret || "",
        qrCode: data.qrCode || "",
        backupCodes: data.backupCodes || [],
      };

      console.log("Normalized 2FA Data:", normalizedData);

      if (!normalizedData.qrCode) {
        throw new Error("QR code data is missing from server response");
      }

      if (!normalizedData.secret && !normalizedData.manualEntryCode) {
        throw new Error("Secret key is missing from server response");
      }

      setTwoFactorData(normalizedData);
      setShow2FAModal(true);
      setStep("setup");
      setVerificationToken("");

      console.log("2FA modal should be opening now...");
    } catch (err: any) {
      console.error("Error enabling 2FA:", err);

      if (err.status === 429) {
        setError(
          "Too many attempts. Please wait a few minutes before trying again.",
        );
      } else if (err.message?.includes("rate limit")) {
        setError("Too many attempts. Please wait before trying again.");
      } else {
        setError(err.message || "Failed to enable two-factor authentication");
      }

      setShow2FAModal(false);
      setTwoFactorData(null);
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyTwoFactor = async () => {
    if (!twoFactorData || !verificationToken) {
      setError("Please enter the verification code");
      return;
    }

    const cleanToken = verificationToken.replace(/\s/g, "");
    if (!/^\d{6}$/.test(cleanToken)) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    try {
      setActionLoading("verify-2fa");
      setError(null);

      console.log("Verifying 2FA with token:", cleanToken);

      const result = await securityService.verifyTwoFactor(cleanToken);

      console.log("2FA verification result:", result);

      if (result.backupCodes && result.backupCodes.length > 0) {
        setBackupCodes(result.backupCodes);
        setStep("backup");
        setSuccessMessage("Two-factor authentication enabled successfully!");
        // Fetch updated security data to reflect 2FA status
        await fetchSecurityData();
      } else {
        setShow2FAModal(false);
        setSuccessMessage("Two-factor authentication enabled successfully!");
        // Fetch updated security data to reflect 2FA status
        await fetchSecurityData();
      }
    } catch (err: any) {
      console.error("2FA Verification Error:", err);
      setError(
        err.message ||
          "Failed to verify two-factor authentication. Please try again.",
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisableTwoFactor = async () => {
    if (!disable2FACode || !disable2FAPassword) {
      setError("Please enter both verification code and your password");
      return;
    }

    const cleanToken = disable2FACode.replace(/\s/g, "");
    if (!/^\d{6}$/.test(cleanToken)) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    // Password length validation
    if (disable2FAPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    try {
      setActionLoading("disable-2fa");
      await securityService.disableTwoFactor(cleanToken, disable2FAPassword);
      setSuccessMessage("Two-factor authentication disabled successfully");
      closeDisable2FAModal();
      await fetchSecurityData();
    } catch (err: any) {
      setError(err.message || "Failed to disable two-factor authentication");
    } finally {
      setActionLoading(null);
    }
  };

  // Reset State When Closing Modal
  const closeDisable2FAModal = () => {
    setShowDisable2FAModal(false);
    setDisable2FACode("");
    setDisable2FAPassword("");
    setShowDisablePassword(false);
  };

  const handleRevokeSession = async (sessionId: string) => {
    try {
      setActionLoading(`session-${sessionId}`);
      await securityService.revokeSession(sessionId);
      setSuccessMessage("Session revoked successfully");
      await fetchActiveSessions();
    } catch (err: any) {
      setError(err.message || "Failed to revoke session");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevokeAllSessions = async () => {
    try {
      setActionLoading("all-sessions");
      const result = await securityService.revokeAllSessions();
      setSuccessMessage(
        `All other sessions (${result.sessionsRevoked}) revoked successfully`,
      );
      await fetchActiveSessions();
    } catch (err: any) {
      setError(err.message || "Failed to revoke all sessions");
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordMessage(null);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ type: "error", text: "New passwords do not match" });
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordMessage({
        type: "error",
        text: "New password must be at least 6 characters long",
      });
      setPasswordLoading(false);
      return;
    }

    try {
      await changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
      );
      setPasswordMessage({
        type: "success",
        text: "Password updated successfully!",
      });
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setShowChangePassword(false);
      await fetchSecurityData();
    } catch (err: any) {
      setPasswordMessage({
        type: "error",
        text: err.message || "Failed to change password",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleResendVerification = async () => {
    try {
      setActionLoading("email-verification");
      if (user?.email) {
        await resendVerificationEmail(user.email);
        setSuccessMessage("Verification email sent successfully");
      }
    } catch (err: any) {
      setError(err.message || "Failed to send verification email");
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateSettings = async (settings: any) => {
    try {
      setActionLoading("settings");
      await securityService.updateSecuritySettings(settings);
      setSuccessMessage("Security settings updated successfully");
      await fetchSecurityData();
    } catch (err: any) {
      setError(err.message || "Failed to update security settings");
    } finally {
      setActionLoading(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage("Copied to clipboard!");
  };

  const downloadBackupCodes = () => {
    const element = document.createElement("a");
    const file = new Blob([backupCodes.join("\n")], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = "FindWork-backup-codes.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const getSecurityStatus = () => {
    if (!securityData || !user) return "loading";

    const issues = [];
    if (!user.emailVerified) issues.push("email not verified");
    if (!securityData.twoFactorEnabled) issues.push("2FA disabled");
    if (securityData.failedLoginAttempts > 0)
      issues.push("failed login attempts");
    if (!user.active) issues.push("account inactive");
    if (securityData.isLocked) issues.push("account locked");

    return issues.length === 0 ? "protected" : "needs_attention";
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "text-red-500";
      case "high":
        return "text-orange-500";
      case "medium":
        return "text-yellow-500";
      case "low":
        return "text-green-500";
      default:
        return "text-gray-500";
    }
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case "mobile":
        return <Smartphone className="h-4 w-4" />;
      case "tablet":
        return <Tablet className="h-4 w-4" />;
      case "desktop":
        return <Laptop className="h-4 w-4" />;
      default:
        return <Laptop className="h-4 w-4" />;
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
    setPasswordMessage(null);
  };

  const securitySettings = [
    {
      icon: Lock,
      title: "Two-Factor Authentication",
      description: "Add an extra layer of security to your account",
      status: securityData?.twoFactorEnabled ? "Enabled" : "Disabled",
      action: securityData?.twoFactorEnabled ? "Manage" : "Enable",
      onClick: securityData?.twoFactorEnabled
        ? () => setShowDisable2FAModal(true)
        : handleEnableTwoFactor,
      loading: actionLoading === "2fa" || actionLoading === "disable-2fa",
    },
    {
      icon: Eye,
      title: "Login Activity",
      description: "Review recent login attempts and devices",
      status: `${securityData?.securityLogs.length} activities`,
      action: "Review",
      onClick: () => setShowSessions(!showSessions),
    },
    {
      icon: Bell,
      title: "Security Alerts",
      description: "Get notified about suspicious activities",
      status: securityData?.securityAlertsEnabled ? "Enabled" : "Disabled",
      action: "Configure",
      onClick: () =>
        handleUpdateSettings({
          enableSecurityAlerts: !securityData?.securityAlertsEnabled,
        }),
      loading: actionLoading === "settings",
    },
    {
      icon: Key,
      title: "Password & Security",
      description: "Change password and update security questions",
      status: `Last changed ${formatDate(securityData?.passwordChangedAt || null)}`,
      action: "Change",
      onClick: () => setShowChangePassword(true),
    },
    {
      icon: UserCheck,
      title: "Session Management",
      description: "Manage active sessions and logout remotely",
      status: `${activeSessions.length} Active`,
      action: "Manage",
      onClick: () => setShowSessions(!showSessions),
    },
    {
      icon: Mail,
      title: "Email Verification",
      description: "Verify your email address for account security",
      status: user?.emailVerified ? "Verified" : "Not Verified",
      action: user?.emailVerified ? "Verified" : "Verify",
      onClick: user?.emailVerified ? undefined : handleResendVerification,
      loading: actionLoading === "email-verification",
    },
  ];

  // Remove the duplicate loading check that was after the skeleton functions
  // You already have it at the top where it should be

  if (error && !securityData) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDark ? "bg-gray-900" : "bg-gradient-to-br from-sky-400 to-blue-600"
        }`}
      >
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">
            Error Loading Security Data
          </h2>
          <p className="text-blue-100 mb-4">{error}</p>
          <button
            onClick={fetchSecurityData}
            className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const securityStatus = getSecurityStatus();

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark
          ? 'bg-gray-900 text-white'
          : 'bg-gradient-to-br from-sky-400 to-blue-600 text-gray-800'
      }`}
    >
      <Helmet>
        <title>Account Security Settings | FindWork</title>
        <meta
          name="description"
          content="Manage your FindWork account security. Enable two-factor authentication and keep your account and personal data safe."
        />
        <link rel="canonical" href="https://findwork.ltd/security" />
      </Helmet>
      
      <div className="max-w-6xl mx-auto">
        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between items-center">
            <span>{error}</span>
            <button
              onClick={clearMessages}
              className="text-red-700 hover:text-red-900"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex justify-between items-center">
            <span>{successMessage}</span>
            <button
              onClick={clearMessages}
              className="text-green-700 hover:text-green-900"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Password Change Modal */}
        {showChangePassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className={`rounded-lg shadow-xl p-6 w-full max-w-md ${
                isDark ? 'bg-gray-800' : 'bg-white'
              }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? 'text-white' : 'text-gray-800'
                  }`}
                >
                  Change Password
                </h3>
                <button
                  onClick={() => setShowChangePassword(false)}
                  className={`p-1 rounded-full ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              {passwordMessage && (
                <div
                  className={`mb-4 p-3 rounded-lg ${
                    passwordMessage.type === 'success'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                  }`}
                >
                  {passwordMessage.text}
                </div>
              )}

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      name="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                      required
                      className={`w-full border rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-sky-400 ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-sky-200 text-gray-900'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-sky-500"
                    >
                      {showCurrentPassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      required
                      className={`w-full border rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-sky-400 ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-sky-200 text-gray-900'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-sky-500"
                    >
                      {showNewPassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      required
                      className={`w-full border rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-sky-400 ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-sky-200 text-gray-900'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-sky-500"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowChangePassword(false)}
                    className={`px-4 py-2 rounded-lg border ${
                      isDark
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg hover:from-sky-600 hover:to-blue-700 disabled:opacity-50 transition-all duration-300"
                  >
                    {passwordLoading
                      ? 'Changing Password...'
                      : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 2FA Setup Modal */}
        {show2FAModal && twoFactorData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className={`rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto ${
                isDark ? 'bg-gray-800' : 'bg-white'
              }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? 'text-white' : 'text-gray-800'
                  }`}
                >
                  {step === 'setup' && 'Setup Two-Factor Authentication'}
                  {step === 'verify' && 'Verify Authentication Code'}
                  {step === 'backup' && 'Backup Codes'}
                </h3>
                <button
                  onClick={() => {
                    setShow2FAModal(false);
                    setStep('setup');
                    setVerificationToken('');
                  }}
                  className={`p-1 rounded-full ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              {step === 'setup' && (
                <div className="space-y-4">
                  <div className="text-center">
                    <QrCode className="h-12 w-12 mx-auto mb-3 text-sky-500" />
                    <p
                      className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                    >
                      Scan this QR code with your authenticator app
                    </p>
                  </div>

                  <div className="flex justify-center">
                    <img
                      src={twoFactorData.qrCode}
                      alt="QR Code"
                      className="w-48 h-48 border rounded-lg"
                    />
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        Manual Entry Code
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={
                            twoFactorData.manualEntryCode ||
                            twoFactorData.secret
                          }
                          readOnly
                          className={`flex-1 border rounded-lg px-3 py-2 focus:outline-none ${
                            isDark
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-gray-100 border-gray-300 text-gray-900'
                          }`}
                        />
                        <button
                          onClick={() =>
                            copyToClipboard(
                              twoFactorData.manualEntryCode ||
                                twoFactorData.secret,
                            )
                          }
                          className={`px-3 py-2 rounded-lg border ${
                            isDark
                              ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                      <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                        <strong>Important:</strong> Save this secret key in a
                        secure location. You'll need it if you lose access to
                        your authenticator app.
                      </p>
                    </div>

                    <button
                      onClick={() => setStep('verify')}
                      className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg py-2 hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
                    >
                      Continue to Verification
                    </button>
                  </div>
                </div>
              )}

              {step === 'verify' && (
                <div className="space-y-4">
                  <div className="text-center">
                    <Lock className="h-12 w-12 mx-auto mb-3 text-sky-500" />
                    <p
                      className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                    >
                      Enter the 6-digit code from your authenticator app
                    </p>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Verification Code
                    </label>
                    <input
                      type="text"
                      value={verificationToken}
                      onChange={(e) => setVerificationToken(e.target.value)}
                      placeholder="000000"
                      maxLength={6}
                      className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-sky-200 text-gray-900'
                      }`}
                    />
                  </div>

                  <button
                    onClick={handleVerifyTwoFactor}
                    disabled={
                      !verificationToken || actionLoading === 'verify-2fa'
                    }
                    className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg py-2 hover:from-sky-600 hover:to-blue-700 disabled:opacity-50 transition-all duration-300"
                  >
                    {actionLoading === 'verify-2fa' ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Verifying...
                      </div>
                    ) : (
                      'Verify & Enable 2FA'
                    )}
                  </button>

                  <button
                    onClick={() => setStep('setup')}
                    className={`w-full py-2 rounded-lg border ${
                      isDark
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Back to QR Code
                  </button>
                </div>
              )}

              {step === 'backup' && backupCodes.length > 0 && (
                <div className="space-y-4">
                  <div className="text-center">
                    <Key className="h-12 w-12 mx-auto mb-3 text-sky-500" />
                    <p
                      className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                    >
                      Save these backup codes in a secure location
                    </p>
                  </div>

                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <p className="text-red-800 dark:text-red-300 text-sm">
                      <strong>Warning:</strong> These codes can only be viewed
                      once. Save them now or you may lose access to your
                      account.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {backupCodes.map((code, index) => (
                      <div
                        key={index}
                        className={`p-2 rounded text-center font-mono text-sm border ${
                          isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-gray-100 border-gray-300 text-gray-900'
                        }`}
                      >
                        {code}
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => copyToClipboard(backupCodes.join('\n'))}
                      className={`flex-1 py-2 rounded-lg border flex items-center justify-center gap-2 ${
                        isDark
                          ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Copy className="h-4 w-4" />
                      Copy All
                    </button>
                    <button
                      onClick={downloadBackupCodes}
                      className={`flex-1 py-2 rounded-lg border flex items-center justify-center gap-2 ${
                        isDark
                          ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </button>
                  </div>

                  <button
                    onClick={async () => {
                      setShow2FAModal(false);
                      setStep('setup');
                      setBackupCodes([]);
                      await fetchSecurityData();
                    }}
                    className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg py-2 hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
                  >
                    Finish Setup
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Disable 2FA Modal */}
        {showDisable2FAModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className={`rounded-lg shadow-xl p-6 w-full max-w-md ${
                isDark ? 'bg-gray-800' : 'bg-white'
              }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? 'text-white' : 'text-gray-800'
                  }`}
                >
                  Disable Two-Factor Authentication
                </h3>
                <button
                  onClick={closeDisable2FAModal}
                  className={`p-1 rounded-full ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="text-center">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-yellow-500" />
                  <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                    Enter your verification code and password to disable
                    two-factor authentication
                  </p>
                </div>

                {/* Verification Code Input */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={disable2FACode}
                    onChange={(e) => setDisable2FACode(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-sky-200 text-gray-900'
                    }`}
                  />
                </div>

                {/* Password Input with Visibility Toggle */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showDisablePassword ? 'text' : 'password'}
                      value={disable2FAPassword}
                      onChange={(e) => setDisable2FAPassword(e.target.value)}
                      placeholder="Enter your password"
                      className={`w-full border rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-sky-400 ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-sky-200 text-gray-900'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowDisablePassword(!showDisablePassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-sky-500"
                    >
                      {showDisablePassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                    <strong>Warning:</strong> Disabling 2FA reduces your account
                    security. You will no longer be required to enter a
                    verification code when logging in.
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={closeDisable2FAModal}
                    className={`px-4 py-2 rounded-lg border ${
                      isDark
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDisableTwoFactor}
                    disabled={
                      !disable2FACode ||
                      !disable2FAPassword ||
                      actionLoading === 'disable-2fa'
                    }
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    {actionLoading === 'disable-2fa' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Disabling...
                      </>
                    ) : (
                      'Disable 2FA'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            <Shield
              className={`h-12 w-12 ${isDark ? 'text-blue-400' : 'text-white'}`}
            />
          </div>
          <h1
            className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-white'}`}
          >
            Security Center
          </h1>
          <p
            className={`text-lg ${isDark ? 'text-gray-300' : 'text-blue-100'}`}
          >
            Manage your account security and privacy settings
          </p>
        </div>

        {/* Security Status Card */}
        <div
          className={`rounded-lg shadow-lg p-6 mb-8 ${
            isDark
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-blue-100'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              {securityStatus === 'protected' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
              )}
              Security Status:{' '}
              {securityStatus === 'protected' ? 'Protected' : 'Needs Attention'}
              {user?.role === 'admin' && (
                <span className="text-xs bg-purple-500 text-white px-2 py-1 rounded ml-2">
                  Admin
                </span>
              )}
            </h2>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                securityStatus === 'protected'
                  ? isDark
                    ? 'bg-green-900 text-green-300'
                    : 'bg-green-100 text-green-800'
                  : isDark
                    ? 'bg-yellow-900 text-yellow-300'
                    : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {securityStatus === 'protected'
                ? 'All Systems Secure'
                : 'Review Recommended'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Last Login
              </p>
              <p className={isDark ? 'text-white' : 'text-gray-800'}>
                {formatDate(securityData?.lastLogin || null)}
              </p>
            </div>
            <div>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Failed Attempts
              </p>
              <p className={isDark ? 'text-white' : 'text-gray-800'}>
                {securityData?.failedLoginAttempts || 0}
              </p>
            </div>
            <div>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Account Status
              </p>
              <p
                className={
                  !user?.active || securityData?.isLocked
                    ? 'text-red-500'
                    : isDark
                      ? 'text-green-400'
                      : 'text-green-600'
                }
              >
                {securityData?.isLocked
                  ? 'Locked'
                  : user?.active
                    ? 'Active'
                    : 'Inactive'}
              </p>
            </div>
            <div>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Member Since
              </p>
              <p className={isDark ? 'text-white' : 'text-gray-800'}>
                {formatDate(user?.createdAt || null)}
              </p>
            </div>
          </div>
        </div>

        {/* Security Settings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {securitySettings.map((setting, index) => (
            <div
              key={index}
              className={`rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-lg ${
                isDark
                  ? 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                  : 'bg-white border border-blue-50 hover:border-blue-200'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      isDark ? 'bg-blue-900/50' : 'bg-blue-100'
                    }`}
                  >
                    <setting.icon
                      className={`h-6 w-6 ${
                        isDark ? 'text-blue-400' : 'text-blue-600'
                      }`}
                    />
                  </div>
                  <div>
                    <h3
                      className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}
                    >
                      {setting.title}
                    </h3>
                    <p
                      className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                    >
                      {setting.description}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span
                  className={`text-sm px-2 py-1 rounded ${
                    isDark
                      ? 'bg-gray-700 text-gray-300'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {setting.status}
                </span>
                <button
                  onClick={setting.onClick}
                  disabled={setting.loading || !setting.onClick}
                  className={`text-sm font-medium px-3 py-1 rounded transition-colors flex items-center gap-2 ${
                    setting.loading || !setting.onClick
                      ? 'opacity-50 cursor-not-allowed'
                      : isDark
                        ? 'text-blue-400 hover:text-blue-300'
                        : 'text-blue-600 hover:text-blue-700'
                  }`}
                >
                  {setting.loading ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    setting.action
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Active Sessions Section */}
        {showSessions && (
          <div
            className={`rounded-lg shadow-lg p-6 mb-6 ${
              isDark
                ? 'bg-gray-800 border border-gray-700'
                : 'bg-white border border-blue-100'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h2
                className={`text-xl font-semibold flex items-center gap-2 ${
                  isDark ? 'text-white' : 'text-gray-800'
                }`}
              >
                <UserCheck className="h-5 w-5" />
                Active Sessions ({activeSessions.length})
              </h2>
              {activeSessions.length > 1 && (
                <button
                  onClick={handleRevokeAllSessions}
                  disabled={actionLoading === 'all-sessions'}
                  className={`text-sm px-3 py-1 rounded border transition-colors flex items-center gap-2 ${
                    actionLoading === 'all-sessions'
                      ? 'opacity-50 cursor-not-allowed'
                      : isDark
                        ? 'border-red-700 text-red-400 hover:bg-red-900/50'
                        : 'border-red-300 text-red-600 hover:bg-red-50'
                  }`}
                >
                  {actionLoading === 'all-sessions' ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Logout All Other Devices'
                  )}
                </button>
              )}
            </div>
            <div className="space-y-3">
              {activeSessions.map((session) => (
                <div
                  key={session.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    isDark ? 'bg-gray-700/50' : 'bg-blue-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-full flex items-center justify-center ${
                        session.isCurrent
                          ? isDark
                            ? 'bg-green-900/50'
                            : 'bg-green-100'
                          : isDark
                            ? 'bg-blue-900/50'
                            : 'bg-blue-100'
                      }`}
                    >
                      {session.isCurrent ? (
                        <CheckCircle
                          className={`h-4 w-4 ${
                            isDark ? 'text-green-400' : 'text-green-600'
                          }`}
                        />
                      ) : (
                        getDeviceIcon(session.deviceType)
                      )}
                    </div>
                    <div>
                      <p
                        className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}
                      >
                        {session.deviceType}
                      </p>
                      <p
                        className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                      >
                        {session.ipAddress} • Created{' '}
                        {formatDate(session.createdAt)}
                      </p>
                    </div>
                  </div>
                  {!session.isCurrent && (
                    <button
                      onClick={() => handleRevokeSession(session.id)}
                      disabled={actionLoading === `session-${session.id}`}
                      className={`p-1 rounded transition-colors ${
                        actionLoading === `session-${session.id}`
                          ? 'opacity-50 cursor-not-allowed'
                          : isDark
                            ? 'text-red-400 hover:bg-red-900/50'
                            : 'text-red-600 hover:bg-red-50'
                      }`}
                    >
                      {actionLoading === `session-${session.id}` ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <LogOut className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Security Activity */}
        <div
          className={`rounded-lg shadow-lg p-6 ${
            isDark
              ? 'bg-gray-800 border border-gray-700'
              : 'bg-white border border-blue-100'
          }`}
        >
          <h2
            className={`text-xl font-semibold mb-4 flex items-center gap-2 ${
              isDark ? 'text-white' : 'text-gray-800'
            }`}
          >
            <RefreshCw className="h-5 w-5" />
            Recent Security Activity
          </h2>
          <div className="space-y-3">
            {securityData?.securityLogs.slice(0, 5).map((log, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  isDark ? 'bg-gray-700/50' : 'bg-blue-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-full ${
                      isDark ? 'bg-gray-600' : 'bg-gray-200'
                    }`}
                  >
                    <Shield
                      className={`h-4 w-4 ${getSeverityColor(log.severity)}`}
                    />
                  </div>
                  <div>
                    <h4
                      className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}
                    >
                      {log.action}
                    </h4>
                    <p
                      className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                    >
                      {log.ipAddress} • {formatDate(log.timestamp)}
                    </p>
                  </div>
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    log.severity === 'critical'
                      ? isDark
                        ? 'bg-red-900 text-red-300'
                        : 'bg-red-100 text-red-800'
                      : log.severity === 'high'
                        ? isDark
                          ? 'bg-orange-900 text-orange-300'
                          : 'bg-orange-100 text-orange-800'
                        : log.severity === 'medium'
                          ? isDark
                            ? 'bg-yellow-900 text-yellow-300'
                            : 'bg-yellow-100 text-yellow-800'
                          : isDark
                            ? 'bg-green-900 text-green-300'
                            : 'bg-green-100 text-green-800'
                  }`}
                >
                  {log.severity}
                </span>
              </div>
            ))}
            {(!securityData?.securityLogs ||
              securityData.securityLogs.length === 0) && (
              <p
                className={`text-center py-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
              >
                No security activity found
              </p>
            )}
          </div>
        </div>

        {/* Security Tips */}
        <div
          className={`mt-8 rounded-lg p-6 ${
            isDark
              ? 'bg-blue-900/20 border border-blue-800'
              : 'bg-blue-100 border border-blue-200'
          }`}
        >
          <h3
            className={`font-semibold mb-3 flex items-center gap-2 ${
              isDark ? 'text-blue-300' : 'text-blue-800'
            }`}
          >
            <AlertTriangle className="h-5 w-5" />
            Security Tips
          </h3>
          <ul
            className={`space-y-2 text-sm ${
              isDark ? 'text-blue-200' : 'text-blue-700'
            }`}
          >
            <li>• Enable two-factor authentication for enhanced security</li>
            <li>• Use a strong, unique password for your account</li>
            <li>• Regularly review your login activity</li>
            <li>• Keep your recovery email and phone number updated</li>
            <li>• Log out from devices you no longer use</li>
            <li>• Be cautious of suspicious emails and links</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SecurityPage;
